import { Camera, Aperture, Users, TrendingUp, ArrowRight, Image } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";

const PhotographyPage = () => {
  const industry = {
    title: "Photography",
    icon: Camera,
    gradient: "from-yellow-500 to-orange-500",
    description: "Enhance portfolios with AI-generated backgrounds, compositions, and artistic variations that expand creative possibilities.",
    stats: "Unlimited creativity"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Aperture className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Creative Backgrounds</h3>
          <p className="text-gray-300 text-sm">Generate stunning backdrops for any shoot</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Image className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Artistic Variations</h3>
          <p className="text-gray-300 text-sm">Explore multiple styles and compositions</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Portfolio Enhancement</h3>
          <p className="text-gray-300 text-sm">Expand your creative offerings to clients</p>
        </CardContent>
      </Card>
    </div>
  );

  const mainContent = (
    <div className="prose prose-invert max-w-none">
      <h2 className="text-3xl font-bold text-white mb-6">Expanding Photographic Creativity</h2>
      
      <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
        <p>
          Photography is an art form constantly pushing creative boundaries. AI-generated imagery offers photographers new tools to enhance their work, create impossible scenarios, and offer clients unlimited creative possibilities.
        </p>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Creative Revolution</h3>
        <p>
          Professional photographers face increasing client demands for unique, standout imagery. AI generation allows photographers to offer services previously impossible - from fantastical backgrounds to artistic interpretations that complement traditional photography.
        </p>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Enhanced Photography Services</h3>
        <ul className="list-disc list-inside space-y-3 text-gray-300">
          <li><strong className="text-white">Background Generation:</strong> Create any backdrop imaginable for portraits</li>
          <li><strong className="text-white">Concept Visualization:</strong> Show clients ideas before expensive shoots</li>
          <li><strong className="text-white">Artistic Interpretations:</strong> Generate paintings and illustrations from photos</li>
          <li><strong className="text-white">Seasonal Variations:</strong> Transform locations across different seasons</li>
          <li><strong className="text-white">Style Experiments:</strong> Explore different photographic styles and eras</li>
        </ul>
        
        <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Professional Applications</h3>
        <p>
          From wedding photographers creating dream backdrops to commercial photographers generating product environments, AI imagery expands what's possible while reducing production costs and time constraints.
        </p>
        
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mt-8">
          <h4 className="text-xl font-semibold text-yellow-400 mb-3 flex items-center">
            <Camera className="w-5 h-5 mr-2" />
            Expand Your Creative Toolkit
          </h4>
          <p className="text-gray-300">
            Combine traditional photography with AI-generated elements to create truly unique and memorable images for your clients.
          </p>
        </div>
      </div>
    </div>
  );

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Image failed to load:', e.currentTarget.src);
    // Add fallback placeholder
    const target = e.target as HTMLImageElement;
    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.log('Image loaded successfully:', e.currentTarget.src);
  };

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Enhanced Photography Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/d23e4a5b-373f-4410-b6b0-facece150139.png" 
              alt="Era-based portrait transformations"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Era-based portrait transformations</p>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/2b9cd936-3ecb-4cb6-89ed-269fb9c35509.png" 
              alt="Professional AI editing workspace"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Professional AI editing workspace</p>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/fb52aed6-ef38-4a96-a98f-1187071692f4.png" 
              alt="Dynamic background selection"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Dynamic background selection</p>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/9013622e-e636-47d4-a323-6295473ad4ae.png" 
              alt="Artistic style transformations"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Artistic style transformations</p>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/593f985d-cb61-4423-9aa8-a3abff3f989d.png" 
              alt="Concept preview collaboration"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Concept preview collaboration</p>
          </CardContent>
        </Card>

        <Card className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
          <div className="aspect-square relative overflow-hidden">
            <img 
              src="/lovable-uploads/d02055ca-e1fe-4142-875f-bace5acc046a.png" 
              alt="Mood and lighting variations"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <CardContent className="p-4">
            <p className="text-sm text-gray-300">Mood and lighting variations</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      heroContent={heroContent}
      mainContent={mainContent}
      imageGallery={imageGallery}
    />
  );
};

export default PhotographyPage;
